"use client";

import React, { createContext, useContext, ReactNode, useState, useEffect, useMemo } from "react";
import { createSafeStorage } from "@/src/lib/typedStorage";
import { LocalStorageModel, LocalStorageKey } from "@/storage.config";
import { getFingerprint } from "@thumbmarkjs/thumbmarkjs";
import { authClient } from "@/src/lib/auth-client";
import { clientLogger as logger } from "@/src/lib/clientLogger";
import { me } from "@/src/api/user";

interface AuthContextType {
  user: any;
  isAuthenticated: boolean;
  isAdmin: boolean;
  status: "loading" | "authenticated" | "unauthenticated";
  permissions: string[];
  anonId?: string;
  signIn: (provider: "github" | "google" | "atlassian") => Promise<void>;
  signOut: () => Promise<void>;
  refetchSession: () => Promise<void>;
}

export const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const AuthProvider = ({ children }: { children: ReactNode }) => {
  const [user, setUser] = useState<any>(null);
  const [status, setStatus] = useState<"loading" | "authenticated" | "unauthenticated">("loading");
  const [anonId, setAnonId] = useState<string | undefined>(undefined);
  
  const localStorageService = useMemo(
    () => createSafeStorage<LocalStorageModel>(() => window.localStorage),
    []
  );
  
  const isAuthenticated = status === "authenticated";
  const permissions = Array.isArray(user?.permissions) ? user.permissions : [];
  // Check if user has admin permissions - either through wildcard "*" or specific ADMIN permission
  const isAdmin = permissions.includes("*") || permissions.includes("ADMIN");

  const fetchSession = async () => {
    try {
      const response = await fetch('/api/auth/get-session', {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
        },
        credentials: 'include',
      });
      
      if (response.ok) {
        const session = await response.json();
        if (session?.user) {
          // Fetch full user data including role and permissions using server function
          try {
            const userData = await me();
            if (userData) {
              // Merge session user with full user data
              const fullUser = {
                ...session.user,
                ...userData,
                permissions: userData?.role?.permissions || [],
                role: userData?.role || null,
              };
              setUser(fullUser);
              setStatus("authenticated");
              return;
            }
          } catch (userError) {
            logger.error({ error: userError }, "Failed to fetch user data");
          }
          // Fallback to session user if me() fails
          setUser(session.user);
          setStatus("authenticated");
        } else {
          setUser(null);
          setStatus("unauthenticated");
        }
      } else {
        setUser(null);
        setStatus("unauthenticated");
      }
    } catch (error) {
      logger.error({ error }, "Failed to fetch session");
      setUser(null);
      setStatus("unauthenticated");
    }
  };

  useEffect(() => {
    fetchSession();
  }, []);

  useEffect(() => {
    let cancelled = false;
    async function ensureAnonId() {
      let id = localStorageService.get(LocalStorageKey.AnonId);
      if (!id) {
        try {
          id = await getFingerprint();
          logger.debug({ anonId: id }, "Generated new anonId");
        } catch (e) {
          id = crypto.randomUUID();
        }
        localStorageService.set(LocalStorageKey.AnonId, id);
      }
      // Set anonId as a cookie for backend access
      if (id) {
        const expires = new Date(Date.now() + 1000 * 60 * 60 * 24 * 365).toUTCString();
        document.cookie = `${LocalStorageKey.AnonId}=${encodeURIComponent(id)}; expires=${expires}; path=/; SameSite=Lax`;
      }
      if (!cancelled) setAnonId(id);
    }
    if (!isAuthenticated) {
      ensureAnonId();
    } else {
      setAnonId(undefined);
    }
    return () => { cancelled = true; };
  }, [isAuthenticated, localStorageService]);

  const handleSignIn = async (provider: "github" | "google" | "atlassian") => {
    // Use better-auth social sign-in
    await authClient.signIn.social({
      provider,
      callbackURL: `${window.location.origin}/login`,
    });
    // Refetch session after sign in
    await fetchSession();
  };

  const handleSignOut = async () => {
    try {
      await authClient.signOut();
      setUser(null);
      setStatus("unauthenticated");
      window.location.href = '/';
    } catch (error) {
      logger.error({ error }, 'Failed to sign out')
    }
  };

  return (
    <AuthContext.Provider value={{ 
      user, 
      isAuthenticated, 
      isAdmin, 
      status, 
      permissions, 
      refetchSession: fetchSession,
      anonId,
      signIn: handleSignIn,
      signOut: handleSignOut,
    }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error("useAuth must be used within an AuthProvider");
  }
  return context;
};
